import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { CalendarIcon, Wrench } from "lucide-react";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface MoldMaintenanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moldId: string;
  moldName: string;
}

export function MoldMaintenanceDialog({
  open,
  onOpenChange,
  moldId,
  moldName,
}: MoldMaintenanceDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    maintenance_type: "",
    description: "",
    duration_minutes: "",
    cost: "",
    parts_replaced: "",
    next_maintenance_date: undefined as Date | undefined,
    notes: "",
    reset_uses: false,
  });

  const maintenanceTypes = [
    { value: "preventive", label: "Mantenimiento Preventivo" },
    { value: "corrective", label: "Mantenimiento Correctivo" },
    { value: "cleaning", label: "Limpieza" },
    { value: "inspection", label: "Inspeccion" },
    { value: "repair", label: "Reparacion" },
    { value: "replacement", label: "Reemplazo de Piezas" },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.maintenance_type) {
      toast.error("Selecciona el tipo de mantenimiento");
      return;
    }

    setLoading(true);
    try {
      // Insert maintenance record
      const { error: maintenanceError } = await api
        .from("mold_maintenance_history")
        .insert({
          mold_id: moldId,
          maintenance_type: formData.maintenance_type,
          description: formData.description || null,
          performed_by: user?.id,
          duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
          cost: formData.cost ? parseFloat(formData.cost) : null,
          parts_replaced: formData.parts_replaced || null,
          next_maintenance_date: formData.next_maintenance_date
            ? format(formData.next_maintenance_date, "yyyy-MM-dd")
            : null,
          notes: formData.notes || null,
        });

      if (maintenanceError) throw maintenanceError;

      // Update mold with new maintenance dates
      const updateData: any = {
        last_maintenance_date: format(new Date(), "yyyy-MM-dd"),
      };

      if (formData.next_maintenance_date) {
        updateData.next_maintenance_date = format(formData.next_maintenance_date, "yyyy-MM-dd");
      }

      if (formData.reset_uses) {
        updateData.current_uses = 0;
      }

      const { error: moldError } = await api
        .from("molds")
        .update(updateData)
        .eq("id", moldId);

      if (moldError) throw moldError;

      toast.success("Mantenimiento registrado correctamente");
      queryClient.invalidateQueries({ queryKey: ["mold", moldId] });
      queryClient.invalidateQueries({ queryKey: ["mold-maintenance", moldId] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || "Error al registrar mantenimiento");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      maintenance_type: "",
      description: "",
      duration_minutes: "",
      cost: "",
      parts_replaced: "",
      next_maintenance_date: undefined,
      notes: "",
      reset_uses: false,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Registrar Mantenimiento
          </DialogTitle>
          <p className="text-sm text-muted-foreground">Molde: {moldName}</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type */}
          <div className="space-y-2">
            <Label>Tipo de Mantenimiento *</Label>
            <Select
              value={formData.maintenance_type}
              onValueChange={(value) =>
                setFormData({ ...formData, maintenance_type: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar tipo" />
              </SelectTrigger>
              <SelectContent>
                {maintenanceTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Descripcion del Trabajo</Label>
            <Textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Describe el trabajo realizado..."
              rows={3}
            />
          </div>

          {/* Duration & Cost */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Duracion (minutos)</Label>
              <Input
                type="number"
                min="1"
                value={formData.duration_minutes}
                onChange={(e) =>
                  setFormData({ ...formData, duration_minutes: e.target.value })
                }
                placeholder="Ej: 60"
              />
            </div>
            <div className="space-y-2">
              <Label>Costo ($)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.cost}
                onChange={(e) =>
                  setFormData({ ...formData, cost: e.target.value })
                }
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Parts Replaced */}
          <div className="space-y-2">
            <Label>Piezas Reemplazadas</Label>
            <Input
              value={formData.parts_replaced}
              onChange={(e) =>
                setFormData({ ...formData, parts_replaced: e.target.value })
              }
              placeholder="Ej: Sellos, tornillos, etc."
            />
          </div>

          {/* Next Maintenance Date */}
          <div className="space-y-2">
            <Label>Proximo Mantenimiento</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.next_maintenance_date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.next_maintenance_date
                    ? format(formData.next_maintenance_date, "PPP", { locale: es })
                    : "Seleccionar fecha"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.next_maintenance_date}
                  onSelect={(date) =>
                    setFormData({ ...formData, next_maintenance_date: date })
                  }
                  disabled={(date) => date < new Date()}
                  initialFocus
                  locale={es}
                />
              </PopoverContent>
            </Popover>
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setFormData({
                    ...formData,
                    next_maintenance_date: addDays(new Date(), 30),
                  })
                }
              >
                +30 dias
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setFormData({
                    ...formData,
                    next_maintenance_date: addDays(new Date(), 60),
                  })
                }
              >
                +60 dias
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setFormData({
                    ...formData,
                    next_maintenance_date: addDays(new Date(), 90),
                  })
                }
              >
                +90 dias
              </Button>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notas Adicionales</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Observaciones, recomendaciones..."
              rows={2}
            />
          </div>

          {/* Reset Uses */}
          <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
            <div>
              <p className="font-medium">Reiniciar contador de usos</p>
              <p className="text-xs text-muted-foreground">
                El contador volvera a 0 despues del mantenimiento
              </p>
            </div>
            <input
              type="checkbox"
              checked={formData.reset_uses}
              onChange={(e) =>
                setFormData({ ...formData, reset_uses: e.target.checked })
              }
              className="w-4 h-4"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Guardando..." : "Registrar Mantenimiento"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
